import { commerceType, inCommerceScope, COMMERCE_SCOPES, type CommerceScope } from '../utils/commerceHistory';
import { ShopifyHistoryNotice, ShopifyOrderHistory, ShopifyFinance } from '../components/ShopifyHistory';
// ===========================================
// WHOLESALE (accounts receivable)
// ===========================================
// Orders you fulfil for wholesale customers with their own pricing →
// a production list → invoices you send and track. Feature-flagged:
// only shown when the restaurant enables the wholesale module.

import { useState, useEffect, Fragment } from 'react';
import ProcurementNav from '../components/ProcurementNav';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { wholesaleApi, menuApi, restaurantApi } from '../services/api';
import { Plus, Send, X, Check, Building2, FileText, CalendarDays, UploadCloud, Pencil, Search, Camera, Trash2, Repeat } from 'lucide-react';
import clsx from 'clsx';
import ImportSpreadsheetModal from '../components/ImportSpreadsheetModal';
import POPipeline from '../components/POPipeline';
import DeductionReconciliation from '../components/DeductionReconciliation';
import ChannelScorecard from '../components/ChannelScorecard';
import CategorySplit from '../components/CategorySplit';
import DeliveryEconomics from '../components/DeliveryEconomics';
import ScanPOModal, { skuLabel } from '../components/ScanPOModal';
import EntityToggle, { EntityView } from '../components/EntityToggle';
import ProofOfDelivery from '../components/wholesale/ProofOfDelivery';
import ArAgingByCategory from '../components/wholesale/ArAgingByCategory';
import ImportPipelineModal from '../components/wholesale/ImportPipelineModal';
import { formatMoney, formatByCurrency } from '../lib/money';
import { displayNotes, CATEGORY_CHIP } from '../utils/cpg';

// Money always speaks its own currency — pass the record's currencyCode and
// $12.50 / €12.50 / 12,50 kr come out right; omitted = USD as before.
const fmt = (c: number, cur?: string | null) => formatMoney(c, cur || 'USD', { exact: true });
const fmtDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString() : '—');
// Sales categories — mirrors the API enum (crm.category on the customer).
const CATEGORY_OPTIONS: Array<[string, string]> = [
  ['retail', 'Retail'], ['ecommerce', 'E-commerce'], ['food_service', 'Food service'], ['distribution', 'Distribution'],
];
const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(CATEGORY_OPTIONS);
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

type Tab = 'pipeline' | 'orders' | 'channels' | 'deductions' | 'production' | 'invoices' | 'customers';

// Wholesale means different things to different businesses, so this page has three
// flavors, chosen by settings.businessType:
//   'cpg'        (e.g. Lovely Bunch) — the full PO suite: Command Center, Channels,
//                Deductions, Scan-a-PO. Distributor POs, freight, sell-through.
//   'bakery'     (e.g. Braided) — standing wholesale accounts; production-first.
//   'restaurant' (e.g. Lords) — the original simple view: orders → invoices.
const CPG_TABS: Tab[] = ['pipeline', 'channels', 'deductions', 'production', 'invoices', 'customers'];
const BASIC_TABS: Tab[] = ['orders', 'production', 'invoices', 'customers'];
const TAB_LABELS: Record<Tab, string> = { pipeline: 'Orders', orders: 'All orders', channels: 'Channels', deductions: 'Deductions', production: 'Production', invoices: 'Invoices', customers: 'Customers' };

export default function WholesalePage() {
  const qc = useQueryClient();
  // Full-restaurant query — same key AND shape as DashboardPage's (a shared key with
  // a different shape is exactly the cache-poisoning bug we just fixed).
  const currentRestaurantId = (() => {
    try { return JSON.parse(localStorage.getItem('manager_current_restaurant') || 'null')?.id || null; } catch { return null; }
  })();
  const { data: restaurant, isPending: bizPending } = useQuery({
    queryKey: ['restaurant-current', currentRestaurantId],
    queryFn: async () => (await restaurantApi.getCurrent()).data?.data || null,
    staleTime: 30 * 1000, refetchOnWindowFocus: true,
  });
  const st = (restaurant?.settings || {}) as any;
  const commerceEnabled = st.shopifyCommerce?.enabled === true;
  const [customerScope, setCustomerScope] = useState<CommerceScope>('wholesale');
  const businessType: 'cpg' | 'bakery' | 'restaurant' =
    st.businessType === 'cpg' || st.businessType === 'bakery' || st.businessType === 'restaurant'
      ? st.businessType
      : st.supplyRatios || st.channelForecast ? 'cpg'          // CPG data present → CPG
      : st.modules?.production ? 'bakery'                       // maker without CPG data
      : 'restaurant';
  const isCpg = businessType === 'cpg';
  const allowedTabs = isCpg ? CPG_TABS : BASIC_TABS;
  const homeTab: Tab = isCpg ? 'pipeline' : businessType === 'bakery' ? 'production' : 'orders';
  // Deep links from the dashboard's quick actions: /wholesale?tab=invoices, ?scan=1
  const params = new URLSearchParams(window.location.search);
  const urlTab = params.get('tab') as Tab | null;
  const [tab, setTab] = useState<Tab>(() => (urlTab === 'orders' ? 'pipeline' : urlTab && CPG_TABS.includes(urlTab) ? urlTab : homeTab));
  useEffect(() => {
    if (!bizPending && !allowedTabs.includes(tab)) setTab(homeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bizPending, businessType, tab]);
  const [banner, setBanner] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const say = (tone: 'success' | 'error', text: string) => setBanner({ tone, text });
  // US / EU / Combined — the lens over Channels and Deductions ("separate,
  // seen together"): each side is its own books, Combined converts at the
  // day's ECB rate with the date on the label.
  const [entityView, setEntityView] = useState<EntityView>('all');

  // ---- data
  const { data: customers = [] } = useQuery({
    queryKey: ['wholesale-customers'],
    queryFn: async () => (await wholesaleApi.getCustomers()).data?.data || [],
  });
  const { data: orders = [] } = useQuery({
    queryKey: ['wholesale-orders'],
    queryFn: async () => (await wholesaleApi.getOrders()).data?.data || [],
  });
  const { data: invoices = [] } = useQuery({
    queryKey: ['wholesale-invoices'],
    queryFn: async () => (await wholesaleApi.getInvoices()).data?.data || [],
  });
  const { data: menuData } = useQuery({
    queryKey: ['menu-items-flat'],
    queryFn: async () => {
      const res = await menuApi.getItems();
      return res.data?.data || res.data || [];
    },
    staleTime: 60_000,
  });
  const menuItems: Array<{ id: string; name: string; priceCents: number }> =
    (Array.isArray(menuData) ? menuData : menuData?.items || []).filter((m: any) => m?.id);

  // Per-customer facts so the Customers tab reads clearly: what they are + what they're worth.
  const custStats = new Map<string, { orders: number; revenue: number; isDistributor: boolean; lastOrderAt: number }>();
  for (const o of orders as any[]) {
    if (o.status === 'cancelled') continue;
    if (commerceEnabled && !inCommerceScope(o, customerScope)) continue;
    const s = custStats.get(o.customerId) || { orders: 0, revenue: 0, isDistributor: false, lastOrderAt: 0 };
    s.orders += 1; s.revenue += o.totalCents || 0;
    const when = new Date(o.fulfillmentDate || o.createdAt).getTime();
    if (Number.isFinite(when) && when > s.lastOrderAt) s.lastOrderAt = when;
    if (o.poNumber || !/direct delivery/i.test(o.notes || '')) s.isDistributor = true;
    custStats.set(o.customerId, s);
  }
  // Open A/R per customer, with the age of the oldest open invoice — the
  // "should I chase them?" column, straight from the invoices below.
  const custAr = new Map<string, { owed: number; oldestDays: number }>();
  for (const i of invoices as any[]) {
    if (i.status === 'paid' || i.status === 'void') continue;
    const owed = (i.totalCents || 0) - (i.amountPaidCents || 0);
    if (owed <= 0) continue;
    const age = Math.floor((Date.now() - new Date(i.dueAt || i.issuedAt).getTime()) / 86400000);
    const row = custAr.get(i.customerId) || { owed: 0, oldestDays: -Infinity };
    row.owed += owed; row.oldestDays = Math.max(row.oldestDays, age);
    custAr.set(i.customerId, row);
  }
  const arBucket = (d: number) => (d <= 0 ? ['not due yet', 'text-slate-400'] : d <= 30 ? ['0–30d', 'text-slate-500'] : d <= 60 ? ['31–60d', 'text-amber-600'] : ['60d+', 'text-rose-600']);

  // Production is rarely due on one exact day — POs land "this week" / "this
  // month" — so the make-list works in ranges. Default: this month.
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const monthRange = (base: Date) => ({ start: iso(new Date(base.getFullYear(), base.getMonth(), 1)), end: iso(new Date(base.getFullYear(), base.getMonth() + 1, 0)) });
  const weekRange = (base: Date) => { const d = new Date(base); const s = new Date(d); s.setDate(d.getDate() - d.getDay()); const e = new Date(s); e.setDate(s.getDate() + 6); return { start: iso(s), end: iso(e) }; };
  const [prodRange, setProdRange] = useState<{ start: string; end: string }>(() => monthRange(new Date()));
  const prodPeriod = (() => {
    const now = new Date();
    if (prodRange.start === prodRange.end) return 'day';
    const m = monthRange(now); if (prodRange.start === m.start && prodRange.end === m.end) return 'month';
    const w = weekRange(now); if (prodRange.start === w.start && prodRange.end === w.end) return 'week';
    return 'custom';
  })();
  const { data: production } = useQuery({
    queryKey: ['wholesale-production', prodRange],
    queryFn: async () => (await wholesaleApi.getProduction(prodRange)).data?.data || null,
    enabled: tab === 'production',
  });
  const prettyDay = (d: string) => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  // ---- Faire (marketplace channel) — connect app credentials, brand authorizes
  // in their Faire browser session, then orders sync as wholesale orders.
  const [showFaire, setShowFaire] = useState(false);
  // The OAuth callback lands here with ?faire=connected|error&reason=… —
  // surface it, then clean the URL so refreshes don't repeat the toast.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const faireResult = q.get('faire');
    if (!faireResult) return;
    if (faireResult === 'connected') say('success', 'Faire connected — hit Sync Faire to pull the orders in.');
    else say('error', `Faire connection failed${q.get('reason') ? `: ${q.get('reason')}` : ''}.`);
    q.delete('faire'); q.delete('reason');
    window.history.replaceState({}, '', `${window.location.pathname}?${q.toString()}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Saved credentials show the finish state; this flag reopens the form to
  // swap in a different app's credentials (e.g. custom app → public app).
  const [faireEditCreds, setFaireEditCreds] = useState(false);
  const [faireForm, setFaireForm] = useState({ applicationToken: '', applicationSecret: '', accessToken: '' });
  const { data: faire } = useQuery({
    queryKey: ['wholesale-faire-status'],
    queryFn: async () => (await wholesaleApi.getFaireStatus()).data?.data || null,
    enabled: tab === 'orders' && isCpg,
  });
  const connectFaire = useMutation({
    mutationFn: () => wholesaleApi.connectFaire(faireForm.applicationToken.trim(), faireForm.applicationSecret.trim(), faireForm.accessToken.trim() || undefined),
    // Don't bounce straight to Faire — the redirect URL must be registered on
    // the app first, so the card flips to its "finish" state with both steps.
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['wholesale-faire-status'] });
      setFaireEditCreds(false);
      const d = res.data?.data || {};
      if (d.authorized) { say('success', 'Faire connected — hit Sync Faire.'); return; }
      // Platform-app flow (no pasted credentials): go straight to Faire's
      // approve page — its redirect URL is registered once, platform-wide.
      if (!faireForm.applicationToken.trim() && d.authorizeUrl) { window.location.href = d.authorizeUrl; return; }
      say('success', 'Credentials saved — now approve on Faire.');
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't save the Faire credentials."),
  });
  const disconnectFaire = useMutation({
    mutationFn: () => wholesaleApi.disconnectFaire(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wholesale-faire-status'] });
      setShowFaire(false);
      say('success', 'Faire disconnected. Orders already imported stay; nothing new will sync.');
    },
    onError: () => say('error', "Couldn't disconnect Faire."),
  });
  const syncFaire = useMutation({
    mutationFn: () => wholesaleApi.syncFaire(),
    onSuccess: (res) => {
      const d = res.data?.data || {};
      qc.invalidateQueries({ queryKey: ['wholesale-orders'] });
      qc.invalidateQueries({ queryKey: ['wholesale-faire-status'] });
      say('success', `Faire sync: ${d.created} new order${d.created === 1 ? '' : 's'} (${d.skippedExisting} already in).${(d.unmatchedLines || []).length ? ` ${d.unmatchedLines.length} lines didn't match a product — check product names/SKUs.` : ''}`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Faire sync failed."),
  });

  // ---- customers
  const emptyCustomer = { name: '', contactName: '', email: '', phone: '', paymentTerms: '', discountPercent: 0, category: '', notes: '' };
  const [customerForm, setCustomerForm] = useState(emptyCustomer);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const createCustomer = useMutation({
    mutationFn: () => wholesaleApi.createCustomer({
      ...customerForm, discountPercent: customerForm.discountPercent || 0,
      category: customerForm.category || undefined, // '' would fail the enum — undefined is dropped
      phone: customerForm.phone.trim() || undefined,
      notes: customerForm.notes.trim() || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wholesale-customers'] });
      setCustomerForm(emptyCustomer);
      setShowCustomerForm(false);
      say('success', 'Customer added.');
    },
    onError: () => say('error', "Couldn't add the customer — check the name and try again."),
  });
  // Search + inline edit for customers
  const [custSearch, setCustSearch] = useState('');
  const [editCustId, setEditCustId] = useState<string | null>(null);
  // entity/currency are optional columns another part of the platform is adding —
  // undefined means the API response didn't carry them, and the form hides them.
  const [editCustForm, setEditCustForm] = useState<{ name: string; contactName: string; email: string; phone: string; paymentTerms: string; discountPercent: number; category: string; notes: string; entity?: string; currency?: string; deliveryTerms: string; paysFreight: boolean; apptRequired: boolean; deliveryInstructions: string; invoiceEmail: string; invoiceText: string; deliveryAddress: string }>({ name: '', contactName: '', email: '', phone: '', paymentTerms: '', discountPercent: 0, category: '', notes: '', deliveryTerms: '', paysFreight: false, apptRequired: false, deliveryInstructions: '', invoiceEmail: '', invoiceText: '', deliveryAddress: '' });
  const beginEditCustomer = (c: any) => {
    setEditCustId(c.id);
    setEditCustForm({
      name: c.name || '', contactName: c.contactName || '', email: c.email || '', phone: c.phone || '', paymentTerms: c.paymentTerms || '', discountPercent: parseFloat(c.discountPercent) || 0,
      category: c.category || '',
      // Legacy rows carry "Category: X · …" in notes — we edit the remainder;
      // the category itself lives in the picker above (already derived).
      notes: displayNotes(c.notes),
      ...('entity' in c ? { entity: c.entity || '' } : {}),
      ...('currency' in c ? { currency: c.currency || '' } : {}),
      deliveryTerms: c.deliveryTerms || '', paysFreight: !!c.paysFreight, apptRequired: !!c.apptRequired,
      deliveryInstructions: c.deliveryInstructions || '', invoiceEmail: c.invoiceEmail || '', invoiceText: c.invoiceText || '', deliveryAddress: c.deliveryAddress || '',
    });
  };
  const saveCustomer = useMutation({
    mutationFn: () => {
      const { entity, currency, ...fields } = editCustForm;
      return wholesaleApi.updateCustomer(editCustId!, {
        ...fields, discountPercent: editCustForm.discountPercent || 0,
        category: editCustForm.category || null, // null clears it (the legacy notes fallback then applies)
        // Only write entity/currency through when the fetched record carried them
        ...(entity ? { entity } : {}),
        ...(currency ? { currency } : {}),
      });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['wholesale-customers'] }); setEditCustId(null); say('success', 'Customer updated.'); },
    onError: () => say('error', "Couldn't update the customer."),
  });
  // Standing weekly order controls (weekday change / stop) on the customer form.
  const setStanding = useMutation({
    mutationFn: ({ id, weekday, items }: { id: string; weekday: number; items: Array<{ menuItemId: string; quantity: number }> }) =>
      wholesaleApi.setStandingWeekly(id, { weekday, items }),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ['wholesale-customers'] }); say('success', `Standing weekly order set — a draft appears every ${res.data?.data?.weekdayName || 'week'}.`); },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't update the standing order."),
  });
  const clearStanding = useMutation({
    mutationFn: (id: string) => wholesaleApi.clearStandingWeekly(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['wholesale-customers'] }); say('success', 'Standing weekly order stopped — existing drafts stay put.'); },
    onError: () => say('error', "Couldn't stop the standing order."),
  });

  // ---- spreadsheet import + PO photo scan (?scan=1 opens the scanner straight from the dashboard)
  const [showImport, setShowImport] = useState(params.get('import') === '1');
  const [showScan, setShowScan] = useState(params.get('scan') === '1');
  // ClickUp pipeline import (customers tab) — accounts + stages + notes from a list export
  const [showPipelineImport, setShowPipelineImport] = useState(false);

  // ---- orders search/filter/sort (the list gets long for wholesale CPGs)
  const [orderSearch, setOrderSearch] = useState('');
  const [orderStatus, setOrderStatus] = useState('');
  const [orderCustFilter, setOrderCustFilter] = useState('');
  const [sortBy, setSortBy] = useState<'orderNumber' | 'account' | 'cases' | 'total' | 'status' | 'date'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const toggleSort = (k: typeof sortBy) => { if (sortBy === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); else { setSortBy(k); setSortDir('desc'); } };

  // ---- new order
  const [orderCustomerId, setOrderCustomerId] = useState('');
  const [orderDate, setOrderDate] = useState('');
  const [orderDelivery, setOrderDelivery] = useState(''); // $ this drop costs to deliver (optional)
  const [orderLines, setOrderLines] = useState<Array<{ menuItemId: string; quantity: number }>>([{ menuItemId: '', quantity: 0 }]);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const createOrder = useMutation({
    mutationFn: () =>
      wholesaleApi.createOrder({
        customerId: orderCustomerId,
        fulfillmentDate: orderDate || undefined,
        deliveryCostCents: orderDelivery !== '' ? Math.round((parseFloat(orderDelivery) || 0) * 100) : undefined,
        items: orderLines.filter((l) => l.menuItemId && l.quantity > 0),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wholesale-orders'] });
      setOrderLines([{ menuItemId: '', quantity: 0 }]);
      setOrderCustomerId('');
      setOrderDate('');
      setOrderDelivery('');
      setShowOrderForm(false);
      say('success', 'Order created as a draft — confirm it when the customer agrees.');
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't create the order."),
  });

  // "Make this a standing weekly order" — copies the order's lines onto the
  // customer; the daily cron then drafts each week's occurrence.
  const makeStanding = useMutation({
    mutationFn: (id: string) => wholesaleApi.makeStandingOrder(id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['wholesale-customers'] });
      const d = res.data?.data || {};
      say('success', `Standing weekly order set for ${d.customerName || 'the customer'} — a draft appears every ${d.weekdayName || 'week'}, you confirm it.`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't set the standing order."),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => wholesaleApi.setOrderStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wholesale-orders'] }),
    onError: () => say('error', "Couldn't update the order."),
  });
  const deleteOrder = useMutation({
    mutationFn: (id: string) => wholesaleApi.deleteOrder(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['wholesale-orders'] }); say('success', 'Order deleted.'); },
    // The API refuses when an invoice references the order — its message says which one.
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't delete the order."),
  });
  // Advance a PO through its lifecycle stage (freight → delivered → invoiced → paid)
  const [advancingId, setAdvancingId] = useState<string | null>(null);
  const advanceStage = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Record<string, unknown> }) => wholesaleApi.setStage(id, updates),
    onMutate: ({ id }) => setAdvancingId(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['wholesale-orders'] }); },
    onSettled: () => setAdvancingId(null),
    onError: () => say('error', "Couldn't update the PO."),
  });

  // ---- edit an order (customer, date, line items)
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{ customerId: string; date: string; deliveryDollars: string; items: Array<{ menuItemId: string; quantity: number; unitPriceCents: number }> }>({ customerId: '', date: '', deliveryDollars: '', items: [] });
  const beginEditOrder = (o: any) => {
    setEditId(o.id);
    setEditForm({
      customerId: o.customerId || '',
      date: o.fulfillmentDate ? new Date(o.fulfillmentDate).toISOString().slice(0, 10) : '',
      deliveryDollars: o.deliveryCostCents != null ? (o.deliveryCostCents / 100).toFixed(2) : '',
      items: (o.items || []).map((it: any) => ({ menuItemId: it.menuItemId, quantity: parseFloat(String(it.quantity)) || 0, unitPriceCents: it.unitPriceCents || 0 })),
    });
  };
  const saveEditOrder = useMutation({
    mutationFn: () => wholesaleApi.updateOrder(editId!, {
      customerId: editForm.customerId,
      fulfillmentDate: editForm.date || null,
      deliveryCostCents: editForm.deliveryDollars === '' ? null : Math.round((parseFloat(editForm.deliveryDollars) || 0) * 100),
      items: editForm.items.filter((l) => l.menuItemId && l.quantity > 0).map((l) => ({ menuItemId: l.menuItemId, quantity: l.quantity, unitPriceCents: l.unitPriceCents || undefined })),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['wholesale-orders'] }); setEditId(null); say('success', 'Order updated.'); },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't update the order."),
  });
  // Partial fulfillment: which order's ship panel is open + per-line qty
  const [shipOrderId, setShipOrderId] = useState<string | null>(null);
  const [shipQty, setShipQty] = useState<Record<string, number>>({});
  const fulfill = useMutation({
    mutationFn: ({ id, items }: { id: string; items: Array<{ orderItemId: string; quantity: number }> }) =>
      wholesaleApi.fulfillOrder(id, items),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wholesale-orders'] });
      setShipOrderId(null);
      setShipQty({});
      say('success', 'Shipment recorded. Invoice it, or ship the rest later.');
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't record the shipment."),
  });

  const makeInvoice = useMutation({
    mutationFn: (orderId: string) => wholesaleApi.createInvoice(orderId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wholesale-orders'] });
      qc.invalidateQueries({ queryKey: ['wholesale-invoices'] });
      setTab('invoices');
      say('success', 'Invoice created — send it from the Invoices tab.');
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't create the invoice."),
  });
  const sendInvoice = useMutation({
    mutationFn: (id: string) => wholesaleApi.sendInvoice(id),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['wholesale-invoices'] });
      say('success', `Invoice emailed to ${res.data?.data?.sentTo || 'the customer'}.`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't email the invoice."),
  });
  const [payingId, setPayingId] = useState<string | null>(null);
  const [payAmount, setPayAmount] = useState('');
  const [payReason, setPayReason] = useState(''); // '' = rest still owed; otherwise the shortfall is a deduction
  const [invSearch, setInvSearch] = useState('');
  // Click-through seeds: PO card → its invoice (invoices tab pre-searched), and
  // invoice card → its PO (Command Center pre-searched). pipelineSeed also keys
  // POPipeline so its internal search state resets on each jump.
  const [pipelineSeed, setPipelineSeed] = useState('');
  const openInvoice = (invoiceNumber: string) => { setInvStatus(''); setInvSearch(invoiceNumber); setTab('invoices'); };
  const openPO = (po: string) => { setPipelineSeed(String(po)); setTab('pipeline'); };
  const [invStatus, setInvStatus] = useState('');
  const recordPayment = useMutation({
    mutationFn: ({ id, cents, deduction }: { id: string; cents: number; deduction?: { amountCents: number; reasonCode: string } }) => wholesaleApi.recordPayment(id, cents, deduction),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['wholesale-invoices'] });
      qc.invalidateQueries({ queryKey: ['wholesale-deductions'] });
      qc.invalidateQueries({ queryKey: ['wholesale-deduction-summary'] });
      setPayingId(null); setPayAmount(''); setPayReason('');
      const d = res.data?.data;
      say('success', d?.status === 'paid'
        ? (d?.deductionCents ? `Settled — ${fmt(d.deductionCents)} logged as a deduction.` : 'Paid in full.')
        : `Payment recorded — ${fmt(d?.balanceCents || 0)} still due.`);
    },
    onError: () => say('error', "Couldn't record the payment."),
  });

  const statusChip = (s: string) =>
    clsx('px-2 py-0.5 rounded-full text-xs font-medium', {
      'bg-slate-100 text-slate-600': s === 'draft',
      'bg-blue-100 text-blue-700': s === 'confirmed',
      'bg-indigo-100 text-indigo-700': s === 'partially_fulfilled',
      'bg-emerald-100 text-emerald-700': s === 'fulfilled' || s === 'paid',
      'bg-amber-100 text-amber-700': s === 'invoiced' || s === 'partial' || s === 'unpaid',
      'bg-red-100 text-red-600': s === 'cancelled' || s === 'void',
    });

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6 pb-safe">
      {showImport && (
        <ImportSpreadsheetModal
          onClose={() => setShowImport(false)}
          onImported={(msg) => { setShowImport(false); qc.invalidateQueries({ queryKey: ['wholesale-orders'] }); qc.invalidateQueries({ queryKey: ['wholesale-customers'] }); say('success', msg); }}
        />
      )}
      {showPipelineImport && (
        <ImportPipelineModal
          customers={customers}
          onClose={() => setShowPipelineImport(false)}
          onError={(m) => say('error', m)}
          onDone={(m) => {
            qc.invalidateQueries({ queryKey: ['wholesale-customers'] });
            qc.invalidateQueries({ queryKey: ['hub-pipeline'] });
            say('success', m);
          }}
        />
      )}
      {showScan && isCpg && (
        <ScanPOModal
          customers={customers}
          menuItems={menuItems}
          onClose={() => setShowScan(false)}
          onError={(m) => say('error', m)}
          onCreated={(msg) => { setShowScan(false); qc.invalidateQueries({ queryKey: ['wholesale-orders'] }); say('success', msg); }}
        />
      )}
      <ProcurementNav />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900">Wholesale</h1>
          <p className="text-sm md:text-base text-slate-500">Wholesale orders and buyer accounts across your sales channels</p>
        </div>
      </div>

      {banner && (
        <div className={clsx('flex items-start justify-between gap-3 rounded-xl border px-4 py-3 text-sm',
          banner.tone === 'success' ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-800')}>
          <p>{banner.text}</p>
          <button onClick={() => setBanner(null)} className="shrink-0 opacity-60 hover:opacity-100"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto">
        {allowedTabs.map((id) => [id, TAB_LABELS[id]] as const).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={clsx('px-4 py-2 rounded-full text-sm font-medium shrink-0',
              tab === id ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}>
            {label}
          </button>
        ))}
      </div>

      {commerceEnabled && <ShopifyHistoryNotice />}
      {/* ---- COMMAND CENTER (PO pipeline) ---- */}
      {tab === 'pipeline' && (
        <div className="space-y-4">
          {commerceEnabled && <ShopifyOrderHistory orders={orders} wholesale />}
          {orders.length === 0 ? (
            <div className="card p-8 text-center text-slate-400">
              No POs yet. Import your tracker from the <button onClick={() => setTab('orders')} className="text-primary-600 font-medium">Orders</button> tab to see them flow through freight → delivery → invoice → payment.
            </div>
          ) : orders.some((o: any) => o.source !== 'shopify') ? (
            <POPipeline key={pipelineSeed} initialQuery={pipelineSeed} orders={orders.filter((o: any) => o.source !== 'shopify')} invoices={invoices as any[]} menuItems={menuItems} say={say} onOpenInvoice={openInvoice} onScan={isCpg ? () => setShowScan(true) : undefined} onAdd={() => setShowOrderForm(true)} onImport={() => setShowImport(true)} onSyncFaire={faire?.authorized ? () => syncFaire.mutate() : undefined} />
          ) : null}
        </div>
      )}

      {/* ---- CHANNELS ---- */}
      {tab === 'channels' && (
        <div className="space-y-4">
          <div className="flex items-center justify-end">
            {!commerceEnabled && <EntityToggle value={entityView} onChange={setEntityView} />}
          </div>
          {/* Native Shopify currencies remain separate. */}
          {commerceEnabled ? <ShopifyFinance orders={orders} channelsOnly wholesale /> : <CategorySplit entity={entityView} />}
          {(!commerceEnabled || orders.some((o:any)=>o.source !== 'shopify')) && <ChannelScorecard onError={(m) => say('error', m)} />}
          {/* What each drop costs and earns — appears once orders carry a delivery cost */}
          {!commerceEnabled && <DeliveryEconomics entity={entityView} />}
        </div>
      )}

      {/* ---- DEDUCTIONS ---- */}
      {tab === 'deductions' && (
        <div className="space-y-4">
          <div className="flex items-center justify-end">
            {!commerceEnabled && <EntityToggle value={entityView} onChange={setEntityView} />}
          </div>
          <DeductionReconciliation onError={(m) => say('error', m)} entity={entityView} />
        </div>
      )}

      {/* ---- ORDERS ---- */}
      {tab === 'orders' && (
        <div className="space-y-4">
          {!showOrderForm && (
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setShowOrderForm(true)} className="btn btn-primary flex items-center gap-2" disabled={customers.length === 0}>
                <Plus className="w-4 h-4" /> New order
              </button>
              {isCpg && (
                <button onClick={() => setShowScan(true)} className="btn btn-secondary flex items-center gap-2">
                  <Camera className="w-4 h-4" /> Scan a PO
                </button>
              )}
              <button onClick={() => setShowImport(true)} className="btn btn-secondary flex items-center gap-2">
                <UploadCloud className="w-4 h-4" /> Import spreadsheet
              </button>
              {isCpg && faire?.authorized && (
                <>
                  <button onClick={() => syncFaire.mutate()} disabled={syncFaire.isPending} className="btn btn-secondary flex items-center gap-2">
                    {syncFaire.isPending ? 'Syncing…' : 'Sync Faire'}
                  </button>
                  <button onClick={() => disconnectFaire.mutate()} disabled={disconnectFaire.isPending}
                    className="btn btn-ghost text-slate-400 hover:text-red-500" title="Disconnect Faire — imported orders stay, nothing new syncs">
                    Disconnect Faire
                  </button>
                </>
              )}
              {isCpg && !faire?.authorized && (
                <button onClick={() => setShowFaire((s) => !s)} className="btn btn-secondary flex items-center gap-2">
                  Connect Faire
                </button>
              )}
            </div>
          )}
          {showFaire && !faire?.authorized && (
            <div className="card p-4 space-y-3">
              {faire?.connected && !faireEditCreds ? (
                // Credentials already saved — only the in-Faire approval remains.
                <>
                  <div>
                    <p className="font-medium text-slate-900">Finish connecting Faire</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Credentials saved. One step left: click below <b>while logged into the brand's Faire account</b> — approve access, and you land back here connected.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <a href={faire.authorizeUrl} className="btn btn-primary">Log in to Faire & approve</a>
                    <button onClick={() => setShowFaire(false)} className="btn btn-ghost">Close</button>
                    <button onClick={() => disconnectFaire.mutate()} disabled={disconnectFaire.isPending} className="btn btn-ghost text-slate-400 hover:text-red-500 ml-auto">Disconnect</button>
                  </div>
                  <p className="text-[11px] text-slate-400">
                    Using app <code className="bg-slate-100 rounded px-1">{faire.applicationToken}</code> ·{' '}
                    <button onClick={() => setFaireEditCreds(true)} className="text-primary-600 font-medium">use different app credentials</button>
                  </p>
                  <p className="text-[11px] text-slate-400">
                    If Faire shows a redirect-URL error on that page, add this address to the app's settings in Faire, then try again:{' '}
                    <button onClick={() => { navigator.clipboard?.writeText(faire.redirectUrl); say('success', 'Redirect URL copied.'); }} className="text-primary-600 font-medium">copy the address</button>
                  </p>
                </>
              ) : faire?.platformApp && !faireEditCreds ? (
                // Platform app configured — connecting is just login-and-approve.
                <>
                  <div>
                    <p className="font-medium text-slate-900">Connect Faire</p>
                    <p className="text-xs text-slate-500 mt-0.5">Log in to Faire and approve access — your orders sync here from then on.</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => connectFaire.mutate()} disabled={connectFaire.isPending} className="btn btn-primary">
                      {connectFaire.isPending ? 'Connecting…' : 'Connect Faire'}
                    </button>
                    <button onClick={() => setShowFaire(false)} className="btn btn-ghost">Cancel</button>
                  </div>
                </>
              ) : (
                // Fallback when no platform app is configured: paste app credentials.
                <>
                  <div>
                    <p className="font-medium text-slate-900">Connect Faire</p>
                    <p className="text-xs text-slate-500 mt-0.5">Paste the Faire app's ID and secret.</p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <input value={faireForm.applicationToken} onChange={(e) => setFaireForm({ ...faireForm, applicationToken: e.target.value })} placeholder="Application ID (apa_…)" className="input" />
                    <input value={faireForm.applicationSecret} onChange={(e) => setFaireForm({ ...faireForm, applicationSecret: e.target.value })} placeholder="Application secret" type="password" className="input" />
                  </div>
                  <input value={faireForm.accessToken} onChange={(e) => setFaireForm({ ...faireForm, accessToken: e.target.value })} placeholder="Access token from the Faire brand portal — works on its own, no app needed" type="password" className="input w-full" />
                  <div className="flex gap-2">
                    <button onClick={() => connectFaire.mutate()}
                      disabled={connectFaire.isPending || (!faireForm.accessToken.trim() && (!faireForm.applicationToken.trim() || !faireForm.applicationSecret.trim()))}
                      className="btn btn-primary">
                      {connectFaire.isPending ? 'Saving…' : 'Save credentials'}
                    </button>
                    <button onClick={() => setShowFaire(false)} className="btn btn-ghost">Cancel</button>
                  </div>
                </>
              )}
            </div>
          )}

          {orders.length === 0 ? (
            <div className="card p-10 text-center text-slate-500">
              <Building2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No wholesale orders yet{customers.length === 0 ? ' — add a customer first' : ''}.</p>
            </div>
          ) : (() => {
            const filtered = (orders as any[]).filter((o) => {
              if (orderStatus && o.status !== orderStatus) return false;
              if (orderCustFilter && o.customerId !== orderCustFilter) return false;
              if (orderSearch) {
                const q = orderSearch.toLowerCase();
                const hay = `${o.orderNumber} ${o.customer?.name || ''} ${o.notes || ''}`.toLowerCase();
                if (!hay.includes(q)) return false;
              }
              return true;
            });
            const totalVal = filtered.reduce((s, o) => s + (o.totalCents || 0), 0);
            const casesOf = (o: any) => (o.items || []).reduce((s: number, it: any) => s + (parseFloat(String(it.quantity)) || 0), 0);
            const keyFns: Record<string, (o: any) => any> = {
              orderNumber: (o) => o.orderNumber || '', account: (o) => o.customer?.name || '',
              cases: casesOf, total: (o) => o.totalCents || 0, status: (o) => o.status || '', date: (o) => o.fulfillmentDate || '',
            };
            const sorted = [...filtered].sort((a, b) => { const va = keyFns[sortBy](a), vb = keyFns[sortBy](b); const c = va < vb ? -1 : va > vb ? 1 : 0; return sortDir === 'asc' ? c : -c; });
            const shown = sorted.slice(0, 500);
            const Th = ({ label, k, align }: { label: string; k?: string; align?: string }) => (
              <th className={`px-3 py-2 font-medium ${align || 'text-left'} ${k ? 'cursor-pointer select-none hover:text-slate-700' : ''}`} onClick={k ? () => toggleSort(k as any) : undefined}>
                {label}{k && sortBy === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
              </th>
            );
            return (
            <div className="space-y-3">
              {/* filter bar + summary */}
              <div className="flex flex-wrap items-center gap-2">
                <input value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)} placeholder="Search account, PO#…" className="input flex-1 min-w-[160px]" />
                <select value={orderCustFilter} onChange={(e) => setOrderCustFilter(e.target.value)} className="input w-auto">
                  <option value="">All accounts</option>
                  {customers.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select value={orderStatus} onChange={(e) => setOrderStatus(e.target.value)} className="input w-auto">
                  <option value="">Any status</option>
                  {['draft','confirmed','partially_fulfilled','fulfilled','invoiced','cancelled'].map((s) => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
                </select>
                {(orderSearch || orderStatus || orderCustFilter) && <button onClick={() => { setOrderSearch(''); setOrderStatus(''); setOrderCustFilter(''); }} className="text-sm text-primary-600 font-medium">Clear</button>}
              </div>
              <p className="text-sm text-slate-500">{filtered.length} order{filtered.length === 1 ? '' : 's'} · {fmt(totalVal)}{filtered.length > shown.length ? ` · showing first ${shown.length}` : ''}</p>

              <div className="card p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase text-slate-400 border-b border-slate-100">
                      <Th label="Order" k="orderNumber" /><Th label="Account" k="account" />
                      <th className="px-3 py-2 font-medium text-left">Items</th>
                      <Th label="Cases" k="cases" align="text-right" /><Th label="Total" k="total" align="text-right" />
                      <Th label="Status" k="status" /><Th label="Due" k="date" />
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                  {shown.map((o: any) => (
                    <Fragment key={o.id}>
                    <tr className="border-b border-slate-50 hover:bg-slate-50/60">
                      <td className="px-3 py-2 font-medium whitespace-nowrap">{o.orderNumber}{/price est/i.test(o.notes || '') && <span className="ml-1 text-[10px] font-normal text-amber-700 bg-amber-50 px-1 py-0.5 rounded" title="No unit price in the source — valued at list price">est.</span>}{o.onConsignment && <span className="ml-1 text-[10px] font-normal text-amber-700 bg-amber-50 px-1 py-0.5 rounded" title="Stock parked at the distributor — not revenue until billed on depletion">consignment</span>}</td>
                      <td className="px-3 py-2 whitespace-nowrap max-w-[180px] truncate">{o.customer?.name}</td>
                      <td className="px-3 py-2 text-slate-500 max-w-[240px] truncate" title={(o.items || []).map((it: any) => `${parseFloat(String(it.quantity))}× ${it.name}`).join(', ')}>{(o.items || []).map((it: any) => `${parseFloat(String(it.quantity))}× ${it.name}`).join(', ')}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{casesOf(o).toLocaleString('en-US')}</td>
                      <td className="px-3 py-2 text-right font-medium whitespace-nowrap">{fmt(o.totalCents)}</td>
                      <td className="px-3 py-2"><span className={statusChip(o.status)}>{o.status.replace('_', ' ')}</span></td>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-500">{fmtDate(o.fulfillmentDate)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={() => editId === o.id ? setEditId(null) : beginEditOrder(o)} className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => makeStanding.mutate(o.id)} disabled={makeStanding.isPending} className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded" title="Make this a standing weekly order — a draft is created each week, you confirm it"><Repeat className="w-3.5 h-3.5" /></button>
                        <button onClick={() => deleteOrder.mutate(o.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded" title="Delete (blocked while an invoice references it)"><Trash2 className="w-3.5 h-3.5" /></button>
                        <ProofOfDelivery orderId={o.id} attachments={o.attachments || []}
                          onSaved={(m) => { qc.invalidateQueries({ queryKey: ['wholesale-orders'] }); say('success', m); }}
                          onError={(m) => say('error', m)} />
                        {o.status === 'draft' && <button onClick={() => setStatus.mutate({ id: o.id, status: 'confirmed' })} className="btn btn-sm btn-secondary ml-1">Confirm</button>}
                        {(o.status === 'confirmed' || o.status === 'partially_fulfilled') && (
                          <button onClick={() => { const init: Record<string, number> = {}; for (const it of (o.items || [])) { const rem = (parseFloat(String(it.quantity)) || 0) - (parseFloat(String(it.fulfilledQuantity || 0)) || 0); if (rem > 0) init[it.id] = rem; } setShipQty(init); setShipOrderId(shipOrderId === o.id ? null : o.id); }} className="btn btn-sm btn-secondary ml-1">Ship</button>
                        )}
                        {(o.status === 'fulfilled' || o.status === 'partially_fulfilled') && (
                          <button onClick={() => makeInvoice.mutate(o.id)} className="btn btn-sm btn-primary ml-1" disabled={makeInvoice.isPending}>Invoice</button>
                        )}
                      </td>
                    </tr>
                {/* Edit panel: change customer, date, and line items */}
                {editId === o.id && (
                  <tr><td colSpan={8} className="bg-slate-50 px-3 py-3">
                  <div className="space-y-2">
                    <div className="grid sm:grid-cols-3 gap-2">
                      <select value={editForm.customerId} onChange={(e) => setEditForm({ ...editForm, customerId: e.target.value })} className="input">
                        {customers.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      <input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} className="input" />
                      <div className="relative" title="What this drop costs you to deliver — powers per-delivery margin">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                        <input type="number" min="0" step="0.01" value={editForm.deliveryDollars} onChange={(e) => setEditForm({ ...editForm, deliveryDollars: e.target.value })} placeholder="Delivery cost" className="input w-full pl-7" />
                      </div>
                    </div>
                    {editForm.items.map((l, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <select value={l.menuItemId} onChange={(e) => { const r = [...editForm.items]; r[i] = { ...r[i], menuItemId: e.target.value }; setEditForm({ ...editForm, items: r }); }} className="input flex-1">
                          <option value="">Product…</option>
                          {menuItems.map((m) => <option key={m.id} value={m.id}>{skuLabel(m)}</option>)}
                        </select>
                        <input type="number" min="0" value={l.quantity || ''} onChange={(e) => { const r = [...editForm.items]; r[i] = { ...r[i], quantity: parseFloat(e.target.value) || 0 }; setEditForm({ ...editForm, items: r }); }} placeholder="qty" className="input w-20" />
                        <input type="number" min="0" step="0.01" value={l.unitPriceCents ? (l.unitPriceCents / 100).toFixed(2) : ''} onChange={(e) => { const r = [...editForm.items]; r[i] = { ...r[i], unitPriceCents: Math.round((parseFloat(e.target.value) || 0) * 100) }; setEditForm({ ...editForm, items: r }); }} placeholder="$/case" className="input w-24" />
                        <button onClick={() => setEditForm({ ...editForm, items: editForm.items.filter((_, x) => x !== i) })} className="p-2 text-red-400 hover:bg-red-50 rounded-lg"><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                    <div className="flex items-center justify-between">
                      <button onClick={() => setEditForm({ ...editForm, items: [...editForm.items, { menuItemId: '', quantity: 0, unitPriceCents: 0 }] })} className="text-sm text-primary-600 font-medium">+ Add line</button>
                      <div className="flex gap-2">
                        <button onClick={() => setEditId(null)} className="btn btn-sm btn-ghost">Cancel</button>
                        <button onClick={() => saveEditOrder.mutate()} disabled={saveEditOrder.isPending} className="btn btn-sm btn-primary">{saveEditOrder.isPending ? 'Saving…' : 'Save'}</button>
                      </div>
                    </div>
                  </div>
                  </td></tr>
                )}
                {/* Ship panel: enter what's going out now, per line */}
                {shipOrderId === o.id && (
                  <tr><td colSpan={8} className="bg-slate-50 px-3 py-3">
                  <div className="space-y-2">
                    {(o.items || []).map((it: any) => {
                      const ordered = parseFloat(String(it.quantity)) || 0;
                      const shipped = parseFloat(String(it.fulfilledQuantity || 0)) || 0;
                      const rem = ordered - shipped;
                      return (
                        <div key={it.id} className="flex items-center justify-between gap-2 text-sm">
                          <span className="text-slate-600">
                            {it.name}
                            <span className="text-slate-400"> · {shipped}/{ordered} shipped{rem > 0 ? `, ${rem} left` : ''}</span>
                          </span>
                          {rem > 0 ? (
                            <input
                              type="number" min="0" max={rem} step="1"
                              value={shipQty[it.id] ?? ''}
                              onChange={(e) => setShipQty({ ...shipQty, [it.id]: Math.min(rem, parseFloat(e.target.value) || 0) })}
                              className="input w-24 py-1.5"
                            />
                          ) : <span className="text-emerald-600 text-xs">done</span>}
                        </div>
                      );
                    })}
                    <div className="flex justify-end gap-2 pt-1">
                      <button onClick={() => { setShipOrderId(null); setShipQty({}); }} className="btn btn-sm btn-ghost">Cancel</button>
                      <button
                        onClick={() => fulfill.mutate({
                          id: o.id,
                          items: Object.entries(shipQty).filter(([, q]) => (q as number) > 0).map(([orderItemId, quantity]) => ({ orderItemId, quantity: quantity as number })),
                        })}
                        className="btn btn-sm btn-primary"
                        disabled={fulfill.isPending || !Object.values(shipQty).some((q) => (q as number) > 0)}
                      >
                        {fulfill.isPending ? 'Recording…' : 'Record shipment'}
                      </button>
                    </div>
                  </div>
                  </td></tr>
                )}
                    </Fragment>
                  ))}
                  </tbody>
                </table>
              </div>
            </div>
            );
          })()}
        </div>
      )}

      {/* NEW ORDER — page-level modal, reachable from the workflow's ＋ */}
      {showOrderForm && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setShowOrderForm(false)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="card p-4 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <select value={orderCustomerId} onChange={(e) => setOrderCustomerId(e.target.value)} className="input">
                  <option value="">Choose customer…</option>
                  {customers.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} className="input" />
                <div className="relative sm:col-span-2" title="What this drop costs you to deliver — optional, powers per-delivery margin">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                  <input type="number" min="0" step="0.01" value={orderDelivery} onChange={(e) => setOrderDelivery(e.target.value)} placeholder="Delivery cost for this drop (optional)" className="input w-full pl-7" />
                </div>
              </div>
              {orderLines.map((l, i) => (
                <div key={i} className="flex items-center gap-2">
                  <select value={l.menuItemId} onChange={(e) => {
                    const rows = [...orderLines]; rows[i] = { ...rows[i], menuItemId: e.target.value }; setOrderLines(rows);
                  }} className="input flex-1">
                    <option value="">Item…</option>
                    {menuItems.map((m) => <option key={m.id} value={m.id}>{skuLabel(m)}</option>)}
                  </select>
                  <input type="number" min="0" step="1" value={l.quantity || ''} placeholder="qty" onChange={(e) => {
                    const rows = [...orderLines]; rows[i] = { ...rows[i], quantity: parseFloat(e.target.value) || 0 }; setOrderLines(rows);
                  }} className="input w-24" />
                  <button onClick={() => setOrderLines(orderLines.filter((_, x) => x !== i))} className="p-2 text-red-400 hover:bg-red-50 rounded-lg"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <div className="flex items-center justify-between">
                <button onClick={() => setOrderLines([...orderLines, { menuItemId: '', quantity: 0 }])} className="text-sm text-primary-600 font-medium">+ Add line</button>
                <div className="flex gap-2">
                  <button onClick={() => setShowOrderForm(false)} className="btn btn-ghost">Cancel</button>
                  <button onClick={() => createOrder.mutate()} className="btn btn-primary"
                    disabled={!orderCustomerId || !orderLines.some((l) => l.menuItemId && l.quantity > 0) || createOrder.isPending}>
                    {createOrder.isPending ? 'Creating…' : 'Create order'}
                  </button>
                </div>
              </div>
              <p className="text-xs text-slate-500">Prices apply automatically from the customer's discount or their price list.</p>
            </div>
          </div>
        </div>
      )}

      {/* ---- PRODUCTION ---- */}
      {tab === 'production' && !commerceEnabled && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <CalendarDays className="w-4 h-4 text-slate-400" />
            <button onClick={() => setProdRange(weekRange(new Date()))}
              className={`px-3 py-1.5 rounded-full text-sm ${prodPeriod === 'week' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              This week
            </button>
            {/* Month pager — any month, not just the current one */}
            {(() => {
              const base = new Date(prodRange.start + 'T12:00:00');
              const m = monthRange(base);
              const isFullMonth = prodRange.start === m.start && prodRange.end === m.end;
              const label = base.toLocaleDateString('en-US', { month: 'long', year: base.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined });
              const shift = (delta: number) => setProdRange(monthRange(new Date(base.getFullYear(), base.getMonth() + delta, 1)));
              return (
                <div className={`flex items-center rounded-full ${isFullMonth ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`}>
                  <button onClick={() => shift(-1)} className="pl-2.5 pr-1.5 py-1.5 text-sm" title="Previous month">‹</button>
                  <button onClick={() => setProdRange(m)} className="px-1 py-1.5 text-sm min-w-[5.5rem] text-center" title="Show this whole month">{isFullMonth ? label : `${label} ▾`}</button>
                  <button onClick={() => shift(1)} className="pr-2.5 pl-1.5 py-1.5 text-sm" title="Next month">›</button>
                </div>
              );
            })()}
            {/* Free range — from x to y, any window */}
            <span className="text-xs text-slate-400">from</span>
            <input type="date" value={prodRange.start} onChange={(e) => e.target.value && setProdRange({ start: e.target.value, end: prodRange.end >= e.target.value ? prodRange.end : e.target.value })} className="input w-40" />
            <span className="text-xs text-slate-400">to</span>
            <input type="date" value={prodRange.end} min={prodRange.start || undefined} onChange={(e) => e.target.value && setProdRange({ start: prodRange.start || e.target.value, end: e.target.value })} className="input w-40" />
            {prodPeriod === 'day' && <span className="text-sm text-slate-500">{prettyDay(prodRange.start)} only</span>}
          </div>
          {!production || production.orderCount === 0 ? (
            <div className="card p-10 text-center text-slate-500">
              <p>Nothing to make {prodPeriod === 'day' ? `on ${prettyDay(prodRange.start)}` : prodPeriod === 'week' ? 'this week' : prodPeriod === 'month' ? 'this month' : 'in this range'} — confirmed orders appear here.</p>
              {/* Never a dead end: offer the nearest dates that DO have orders */}
              {((production?.nextDates?.length || 0) > 0 || (production?.prevDates?.length || 0) > 0) && (
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-sm">
                  {(production?.nextDates || []).length > 0 && <span className="text-slate-400">Next due:</span>}
                  {(production?.nextDates || []).map((d: string) => (
                    <button key={d} onClick={() => setProdRange({ start: d, end: d })} className="px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700">{prettyDay(d)}</button>
                  ))}
                  {(production?.prevDates || []).length > 0 && <span className="text-slate-400 ml-2">Recent:</span>}
                  {(production?.prevDates || []).map((d: string) => (
                    <button key={d} onClick={() => setProdRange({ start: d, end: d })} className="px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700">{prettyDay(d)}</button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="card p-4">
                <h3 className="font-semibold mb-3">What to make</h3>
                <div className="space-y-1.5">
                  {production.products.map((p: any) => (
                    <div key={p.name} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">{p.name}</span>
                      <span className="font-bold text-slate-900">{p.quantity}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card p-4">
                <h3 className="font-semibold mb-3">By customer</h3>
                <div className="space-y-3">
                  {production.byCustomer.map((c: any, i: number) => (
                    <div key={i}>
                      <p className="text-sm font-medium text-slate-800">{c.customer} <span className="text-slate-400">({c.orderNumber})</span></p>
                      <p className="text-xs text-slate-500">{c.items.map((it: any) => `${it.quantity}× ${it.name}`).join(' · ')}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'production' && commerceEnabled && <div className="card p-6 space-y-3"><h2 className="font-semibold">Production planning</h2><p className="text-sm text-slate-500">Shopify shows customer demand, but does not specify manufacturing batches or warehouse stock. Review outstanding orders in Fulfillment; connect the warehouses to plan replenishment.</p><a href="/production?tab=orders" className="text-primary-600 text-sm font-medium">Open fulfillment →</a></div>}
      {/* ---- INVOICES ---- */}
      {tab === 'invoices' && (
        <div className="space-y-4">
          {commerceEnabled && <ShopifyOrderHistory orders={orders} payments wholesale />}
          {invoices.length === 0 ? (
            <div className="card p-10 text-center text-slate-500">No issued customer invoices imported yet. Connect accounting or create an invoice for a Mise-managed order.</div>
          ) : (<>
            {/* Who owes what, oldest first — the AR answer their spreadsheet can't give */}
            {(() => {
              const unpaid = invoices.filter((i: any) => i.status !== 'paid' && i.status !== 'void');
              if (unpaid.length === 0) return null;
              // Owed amounts stay per currency — dollars and kroner never sum
              // into one number here; each customer's line speaks its own money.
              const addCur = (m: Record<string, number>, i: any) => { const c = String(i.currencyCode || 'USD').toUpperCase(); m[c] = (m[c] || 0) + (i.totalCents || 0) - (i.amountPaidCents || 0); };
              const byCust = new Map<string, { name: string; owedBy: Record<string, number>; count: number; oldest: number }>();
              for (const i of unpaid) {
                const key = i.customerId; const when = new Date(i.dueAt || i.issuedAt).getTime();
                const row = byCust.get(key) || { name: i.customer?.name || '—', owedBy: {}, count: 0, oldest: Infinity };
                addCur(row.owedBy, i); row.count += 1; row.oldest = Math.min(row.oldest, when);
                byCust.set(key, row);
              }
              const all = [...byCust.values()].sort((a, b) => a.oldest - b.oldest);
              const days = (t: number) => Math.floor((Date.now() - t) / 86400000);
              // "Oldest first" only makes sense for money that's actually late — accounts
              // whose invoices aren't due yet go in their own group below, as "due in Xd".
              const rows = all.filter((r) => days(r.oldest) >= 0);
              const notDue = all.filter((r) => days(r.oldest) < 0);
              const totalOwedBy: Record<string, number> = {};
              for (const i of unpaid) addCur(totalOwedBy, i);
              // Their two sheets disagree on some invoices: the PO tracker's Payment-Received
              // flag says unpaid, but the Invoice Tracker records a payment. We keep the flags
              // as primary and surface the conflict rather than silently picking a side.
              const disputed = unpaid.filter((i: any) => /disputed/i.test(i.notes || ''));
              const disputedBy: Record<string, number> = {};
              for (const i of disputed) addCur(disputedBy, i);
              const lessDisputed: Record<string, number> = { ...totalOwedBy };
              for (const [c, v] of Object.entries(disputedBy)) lessDisputed[c] = (lessDisputed[c] || 0) - v;
              return (
                <div className="card p-0 overflow-hidden">
                  <div className="px-4 py-2.5 bg-slate-50 flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-700">Owed to you — oldest first</p>
                    <p className="text-sm font-bold text-slate-900">{formatByCurrency(totalOwedBy, { exact: true })}</p>
                  </div>
                  <div className="divide-y divide-slate-50">
                    {rows.map((r) => {
                      const d = days(r.oldest);
                      return (
                        <div key={r.name} className="px-4 py-2 flex items-center justify-between gap-3 text-sm">
                          <span className="text-slate-700 truncate">{r.name} <span className="text-xs text-slate-400">· {r.count} invoice{r.count === 1 ? '' : 's'}</span></span>
                          <span className="shrink-0"><span className="font-semibold text-slate-900">{formatByCurrency(r.owedBy, { exact: true })}</span> <span className={clsx('text-xs', d > 60 ? 'text-rose-600' : d > 30 ? 'text-amber-600' : 'text-slate-400')}>· oldest {d}d</span></span>
                        </div>
                      );
                    })}
                  </div>
                  {notDue.length > 0 && (
                    <>
                      <div className="px-4 py-1.5 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-400">Not yet due</div>
                      <div className="divide-y divide-slate-50">
                        {notDue.map((r) => (
                          <div key={r.name} className="px-4 py-2 flex items-center justify-between gap-3 text-sm">
                            <span className="text-slate-600 truncate">{r.name} <span className="text-xs text-slate-400">· {r.count} invoice{r.count === 1 ? '' : 's'}</span></span>
                            <span className="shrink-0"><span className="font-medium text-slate-700">{formatByCurrency(r.owedBy, { exact: true })}</span> <span className="text-xs text-slate-400">· due in {-days(r.oldest)}d</span></span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {disputed.length > 0 && (
                    <div className="px-4 py-2.5 bg-amber-50/60 border-t border-amber-100 text-xs text-amber-800">
                      ⚠ Your Invoice Tracker records payments for <b>{disputed.length}</b> of these invoices ({formatByCurrency(disputedBy, { exact: true })}) even though the PO tracker's Payment-Received flag says unpaid. On the Invoice-Tracker basis you'd be owed <b>{formatByCurrency(lessDisputed, { exact: true })}</b>. Both views kept — the disputed ones are marked below.
                    </div>
                  )}
                </div>
              );
            })()}
            {/* The same open money, sliced by sales channel — where the aging risk sits */}
            <ArAgingByCategory invoices={invoices as any[]} customers={customers as any[]} />
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={invSearch} onChange={(e) => setInvSearch(e.target.value)} placeholder="Search invoice # or customer…" className="w-full text-sm rounded-xl border border-slate-200 pl-9 pr-3 py-2.5 focus:ring-2 focus:ring-primary-400 focus:border-primary-400" />
              </div>
              <select value={invStatus} onChange={(e) => setInvStatus(e.target.value)} className="input w-auto text-sm">
                <option value="">All statuses</option>
                <option value="unpaid">Unpaid</option>
                <option value="partial">Partial</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            {invoices
              .filter((inv: any) => !invStatus || inv.status === invStatus)
              .filter((inv: any) => { const n = invSearch.trim().toLowerCase(); return !n || [inv.invoiceNumber, inv.customer?.name].some((v) => String(v || '').toLowerCase().includes(n)); })
              .map((inv: any) => (
            <div key={inv.id} className="card p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{inv.invoiceNumber}</span>
                  <span className={statusChip(inv.status)}>{inv.status}</span>
                  {/disputed/i.test(inv.notes || '') && <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded" title="Invoice Tracker records a payment; PO tracker's Payment-Received flag says unpaid">status disputed</span>}
                  {inv.sentAt && <span className="text-xs text-slate-400">sent {fmtDate(inv.sentAt)}</span>}
                  {inv.status !== 'paid' && inv.status !== 'void' && (() => {
                    const overdueDays = inv.dueAt ? Math.floor((Date.now() - new Date(inv.dueAt).getTime()) / 86400000) : 0;
                    if (overdueDays > 0) return <span className="text-[10px] font-medium text-red-700 bg-red-50 px-1.5 py-0.5 rounded">{overdueDays}d overdue</span>;
                    const outDays = Math.floor((Date.now() - new Date(inv.sentAt || inv.issuedAt || inv.createdAt).getTime()) / 86400000);
                    if (outDays >= 0) return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${outDays > 14 ? 'text-amber-700 bg-amber-50' : 'text-slate-500 bg-slate-100'}`}>{outDays}d outstanding</span>;
                    return null;
                  })()}
                </div>
                <p className="text-sm text-slate-500">
                  {inv.customer?.name} · {fmt(inv.totalCents, inv.currencyCode)}{inv.amountPaidCents > 0 && inv.status !== 'paid' ? ` · ${fmt(inv.totalCents - inv.amountPaidCents, inv.currencyCode)} due` : ''}
                  {(() => {
                    // The PO this invoice bills — click through to it on the Command Center
                    const po = (orders as any[]).find((o: any) => o.id === inv.orderId)?.poNumber;
                    return po ? <button onClick={() => openPO(String(po))} className="ml-1.5 text-[11px] text-primary-700 bg-primary-50 hover:bg-primary-100 px-1.5 py-0.5 rounded" title="Open this PO on the Command Center">PO {String(po).replace(/^PO\s*/i, '')}</button> : null;
                  })()}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {inv.status !== 'paid' && inv.status !== 'void' && (
                  payingId === inv.id ? (() => {
                    const balance = inv.totalCents - inv.amountPaidCents;
                    const cents = Math.round(parseFloat((payAmount || '0').replace(',', '.')) * 100) || balance;
                    const shortfall = Math.max(0, balance - cents);
                    return (
                      <div className="flex flex-col items-end gap-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400">Received {String(inv.currencyCode || 'USD') === 'USD' ? '$' : String(inv.currencyCode || 'USD') === 'EUR' ? '€' : 'kr'}</span>
                          <input type="number" min="0" step="0.01" autoFocus value={payAmount} placeholder={(balance / 100).toFixed(2)}
                            onChange={(e) => setPayAmount(e.target.value)} className="input w-28 text-sm py-1.5" />
                          <button onClick={() => recordPayment.mutate({ id: inv.id, cents, deduction: shortfall > 0 && payReason ? { amountCents: shortfall, reasonCode: payReason } : undefined })}
                            className="btn btn-sm btn-primary"><Check className="w-3.5 h-3.5" /></button>
                          <button onClick={() => { setPayingId(null); setPayAmount(''); setPayReason(''); }} className="btn btn-sm btn-ghost"><X className="w-3.5 h-3.5" /></button>
                        </div>
                        {shortfall > 0 && (
                          <div className="flex items-center gap-1.5 text-xs text-slate-500">
                            <span>Short {fmt(shortfall, inv.currencyCode)} —</span>
                            <select value={payReason} onChange={(e) => setPayReason(e.target.value)} className="input text-xs py-1 w-auto">
                              <option value="">still owed</option>
                              <option value="promo">deduction · promo</option>
                              <option value="advertising">deduction · co-op advertising</option>
                              <option value="slotting">deduction · slotting</option>
                              <option value="samples">deduction · samples</option>
                              <option value="catalog">deduction · catalog fee</option>
                              <option value="chargeback">deduction · chargeback</option>
                              <option value="freight">deduction · freight</option>
                              <option value="shortpay">deduction · short-pay</option>
                              <option value="other">deduction · other</option>
                            </select>
                          </div>
                        )}
                      </div>
                    );
                  })() : (
                    <>
                      <button onClick={() => sendInvoice.mutate(inv.id)} className="btn btn-sm btn-secondary flex items-center gap-1.5" disabled={sendInvoice.isPending}>
                        <Send className="w-3.5 h-3.5" /> {inv.sentAt ? 'Resend' : 'Send'}
                      </button>
                      <button onClick={() => setPayingId(inv.id)} className="btn btn-sm btn-primary">Record payment</button>
                    </>
                  )
                )}
              </div>
            </div>
          ))}
          </>)}
        </div>
      )}

      {/* ---- CUSTOMERS ---- */}
      {tab === 'customers' && (
        <div className="space-y-4">
          {!showCustomerForm ? (
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setShowCustomerForm(true)} className="btn btn-primary flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add customer
              </button>
              <button onClick={() => setShowPipelineImport(true)} className="btn btn-secondary flex items-center gap-2"
                title="A ClickUp list export becomes wholesale accounts with their stage and notes">
                <UploadCloud className="w-4 h-4" /> Import pipeline (CSV)
              </button>
            </div>
          ) : (
            <div className="card p-4 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={customerForm.name} onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })} placeholder="Business name *" className="input" />
                <input value={customerForm.contactName} onChange={(e) => setCustomerForm({ ...customerForm, contactName: e.target.value })} placeholder="Contact name" className="input" />
                <input value={customerForm.email} onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })} placeholder="Email (for invoices)" className="input" />
                <input value={customerForm.phone} onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })} placeholder="Phone" className="input" />
                <input value={customerForm.paymentTerms} onChange={(e) => setCustomerForm({ ...customerForm, paymentTerms: e.target.value })} placeholder="Terms (e.g. Net 30)" className="input" />
                <div className="relative">
                  <input type="number" min="0" max="100" value={customerForm.discountPercent || ''} onChange={(e) => setCustomerForm({ ...customerForm, discountPercent: parseFloat(e.target.value) || 0 })}
                    placeholder="Discount off menu prices" className="input w-full pr-8" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                </div>
                <select value={customerForm.category} onChange={(e) => setCustomerForm({ ...customerForm, category: e.target.value })} className="input" title="Groups this account's revenue on Channels — Sales by category">
                  <option value="">Sales category…</option>
                  {CATEGORY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <textarea value={customerForm.notes} onChange={(e) => setCustomerForm({ ...customerForm, notes: e.target.value })} placeholder="Notes (anything worth remembering about this account)" className="input w-full min-h-[52px]" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowCustomerForm(false)} className="btn btn-ghost">Cancel</button>
                <button onClick={() => createCustomer.mutate()} className="btn btn-primary" disabled={!customerForm.name.trim() || createCustomer.isPending}>
                  {createCustomer.isPending ? 'Saving…' : 'Save customer'}
                </button>
              </div>
            </div>
          )}
          {commerceEnabled && <label className="flex items-center gap-2 text-sm text-slate-600">Customer orders <select aria-label="Customer order type" className="input w-auto" value={customerScope} onChange={e=>setCustomerScope(e.target.value as CommerceScope)}>{COMMERCE_SCOPES.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}</select></label>}
          {customers.length === 0 ? (
            <div className="card p-10 text-center text-slate-500">
              No wholesale customers yet — add one above, or they arrive automatically when you import an order spreadsheet or scan a PO.
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={custSearch} onChange={(e) => setCustSearch(e.target.value)} placeholder="Search customers…" className="w-full text-sm rounded-xl border border-slate-200 pl-9 pr-3 py-2.5 focus:ring-2 focus:ring-primary-400 focus:border-primary-400" />
              </div>
              <div className="card p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase text-slate-400 border-b border-slate-100">
                      <th className="px-3 py-2 font-medium text-left">Customer</th>
                      <th className="px-3 py-2 font-medium text-left">{commerceEnabled ? 'Sales type' : 'Category'}</th>
                      <th className="px-3 py-2 font-medium text-left">Terms</th>
                      <th className="px-3 py-2 font-medium text-left" title="Standing weekly order — a draft is created each week, you confirm it">Standing</th>
                      <th className="px-3 py-2 font-medium text-right" title="Open invoices: amount still owed, with the age of the oldest">Open A/R</th>
                      <th className="px-3 py-2 font-medium text-left">Last order</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers
                      .filter((c: any) => !commerceEnabled || customerScope === 'all' || orders.some((o:any)=>o.customerId === c.id && inCommerceScope(o,customerScope)))
                      .filter((c: any) => { const n = custSearch.trim().toLowerCase(); return !n || [c.name, c.contactName, c.email, c.paymentTerms].some((v) => String(v || '').toLowerCase().includes(n)); })
                      .map((c: any) => {
                        const s = custStats.get(c.id);
                        const ar = custAr.get(c.id);
                        const noteText = displayNotes(c.notes);
                        return (
                          <Fragment key={c.id}>
                            <tr className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer"
                              onClick={() => (editCustId === c.id ? setEditCustId(null) : beginEditCustomer(c))}>
                              <td className="px-3 py-2.5 max-w-[240px]">
                                <p className="font-medium text-slate-900 truncate">{c.name}</p>
                                <p className="text-xs text-slate-400 truncate">
                                  {[c.contactName, c.email].filter(Boolean).join(' · ') || (noteText ? noteText : 'No contact yet')}
                                </p>
                              </td>
                              <td className="px-3 py-2.5 whitespace-nowrap">
                                {commerceEnabled ? <span className="text-xs text-slate-600">{[...new Set(orders.filter((o:any)=>o.customerId===c.id).map((o:any)=>COMMERCE_SCOPES.find(s=>s.value===commerceType(o))?.label))].join(' · ') || 'No orders yet'}</span> : (c as any).category
                                  ? <span className={clsx('px-1.5 py-0.5 rounded text-[10px] font-medium', CATEGORY_CHIP[(c as any).category] || 'bg-slate-100 text-slate-600')} title="Sales category — groups revenue on Channels">{CATEGORY_LABELS[(c as any).category] || (c as any).category}</span>
                                  : <span className="text-slate-300">—</span>}
                              </td>
                              <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">
                                {c.paymentTerms || <span className="text-slate-300">—</span>}
                                {parseFloat(c.discountPercent) > 0 && <span className="text-xs text-slate-400"> · {parseFloat(c.discountPercent)}% off</span>}
                              </td>
                              <td className="px-3 py-2.5 whitespace-nowrap">
                                {(c as any).standingWeekly
                                  ? <span className="text-[10px] text-primary-700 bg-primary-50 px-1.5 py-0.5 rounded inline-flex items-center gap-1" title={`Standing weekly order — drafted every ${WEEKDAYS[(c as any).standingWeekly.weekday]}`}><Repeat className="w-2.5 h-2.5" />{WEEKDAYS[(c as any).standingWeekly.weekday]}s</span>
                                  : <span className="text-slate-300">—</span>}
                              </td>
                              <td className="px-3 py-2.5 text-right whitespace-nowrap">
                                {ar ? (() => { const [label, color] = arBucket(ar.oldestDays); return (
                                  <span><span className="font-medium text-slate-900">{fmt(ar.owed)}</span> <span className={clsx('text-xs', color)}>· {label}</span></span>
                                ); })() : <span className="text-slate-300">—</span>}
                              </td>
                              <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">
                                {s?.lastOrderAt ? fmtDate(new Date(s.lastOrderAt).toISOString()) : <span className="text-slate-300">no orders yet</span>}
                                {s && <span className="text-xs text-slate-400"> · {s.orders} total</span>}
                              </td>
                              <td className="px-3 py-2.5 text-right">
                                <button onClick={(e) => { e.stopPropagation(); editCustId === c.id ? setEditCustId(null) : beginEditCustomer(c); }} title="Edit customer" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><Pencil className="w-4 h-4" /></button>
                              </td>
                            </tr>
                            {editCustId === c.id && (
                              <tr><td colSpan={7} className="bg-slate-50 px-4 py-4">
                                <div className="space-y-3">
                                  <div className="grid sm:grid-cols-2 gap-3">
                                    <input value={editCustForm.name} onChange={(e) => setEditCustForm({ ...editCustForm, name: e.target.value })} placeholder="Business name *" className="input" />
                                    <input value={editCustForm.contactName} onChange={(e) => setEditCustForm({ ...editCustForm, contactName: e.target.value })} placeholder="Contact name" className="input" />
                                    <input value={editCustForm.email} onChange={(e) => setEditCustForm({ ...editCustForm, email: e.target.value })} placeholder="Email (for invoices)" className="input" />
                                    <input value={editCustForm.phone} onChange={(e) => setEditCustForm({ ...editCustForm, phone: e.target.value })} placeholder="Phone" className="input" />
                                    <input value={editCustForm.paymentTerms} onChange={(e) => setEditCustForm({ ...editCustForm, paymentTerms: e.target.value })} placeholder="Terms (e.g. Net 30)" className="input" />
                                    <div className="relative">
                                      <input type="number" min="0" max="100" value={editCustForm.discountPercent || ''} onChange={(e) => setEditCustForm({ ...editCustForm, discountPercent: parseFloat(e.target.value) || 0 })} placeholder="Discount off menu prices" className="input w-full pr-8" />
                                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                                    </div>
                                    <select value={editCustForm.category} onChange={(e) => setEditCustForm({ ...editCustForm, category: e.target.value })} className="input" title="Groups this account's revenue on Channels — Sales by category">
                                      <option value="">Sales category…</option>
                                      {CATEGORY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </select>
                                    {/* Billing entity & currency — only when the account record carries them */}
                                    {(editCustForm.entity !== undefined || editCustForm.currency !== undefined) && (
                                      <div className="grid grid-cols-2 gap-3">
                                        {editCustForm.entity !== undefined && (
                                          <select value={editCustForm.entity} onChange={(e) => setEditCustForm({ ...editCustForm, entity: e.target.value })} className="input" title="Which of your entities bills this customer">
                                            <option value="">Entity…</option>
                                            <option value="us">US</option>
                                            <option value="eu">EU</option>
                                          </select>
                                        )}
                                        {editCustForm.currency !== undefined && (
                                          <select value={editCustForm.currency} onChange={(e) => setEditCustForm({ ...editCustForm, currency: e.target.value })} className="input" title="The currency their orders and invoices are in">
                                            <option value="">Currency…</option>
                                            <option value="USD">USD</option>
                                            <option value="EUR">EUR</option>
                                            <option value="DKK">DKK</option>
                                          </select>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <textarea value={editCustForm.notes} onChange={(e) => setEditCustForm({ ...editCustForm, notes: e.target.value })} placeholder="Notes (anything worth remembering about this account)" className="input w-full min-h-[52px]" />
                                  {/* Standing weekly order — set from any order ("Repeat weekly"); managed here */}
                                  {(() => {
                                    const sw = (c as any).standingWeekly;
                                    if (!sw) return (
                                      <p className="text-xs text-slate-400">No standing weekly order — on any order, tap the <Repeat className="w-3 h-3 inline -mt-0.5" /> action to repeat it weekly as an auto-created draft.</p>
                                    );
                                    return (
                                      <div className="rounded-xl bg-white border border-slate-200 p-3 space-y-2">
                                        <div className="flex flex-wrap items-center gap-2 text-sm">
                                          <Repeat className="w-3.5 h-3.5 text-primary-600" />
                                          <span className="font-medium text-slate-700">Standing weekly order</span>
                                          <span className="text-slate-500">— drafted every</span>
                                          <select value={sw.weekday} onChange={(e) => setStanding.mutate({ id: c.id, weekday: parseInt(e.target.value, 10), items: sw.items })} className="input w-auto py-1 text-sm">
                                            {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                                          </select>
                                          <button onClick={() => clearStanding.mutate(c.id)} disabled={clearStanding.isPending} className="ml-auto text-xs text-slate-400 hover:text-red-500 font-medium">Stop</button>
                                        </div>
                                        <p className="text-xs text-slate-500">
                                          {sw.items.map((it: any) => { const m = menuItems.find((x) => x.id === it.menuItemId); return `${it.quantity}× ${m?.name || 'product'}`; }).join(' · ')}
                                        </p>
                                        <p className="text-[11px] text-slate-400">Each week's order arrives as a draft — nothing confirms without you.</p>
                                      </div>
                                    );
                                  })()}
                                  {/* Invoicing & delivery profile — powers the Send Invoice step */}
                                  <p className="text-xs font-medium text-slate-500 pt-1">Invoicing &amp; delivery profile</p>
                                  <div className="grid sm:grid-cols-2 gap-3">
                                    <select value={editCustForm.deliveryTerms} onChange={(e) => setEditCustForm({ ...editCustForm, deliveryTerms: e.target.value })} className="input">
                                      <option value="">Delivery terms…</option>
                                      <option value="delivered">Delivered (we ship)</option>
                                      <option value="fob">FOB (they pick up)</option>
                                    </select>
                                    <input value={editCustForm.invoiceEmail} onChange={(e) => setEditCustForm({ ...editCustForm, invoiceEmail: e.target.value })} placeholder="Invoice email(s), comma-separated" className="input" />
                                    <label className="text-sm text-slate-600 flex items-center gap-2"><input type="checkbox" checked={editCustForm.paysFreight} onChange={(e) => setEditCustForm({ ...editCustForm, paysFreight: e.target.checked })} /> They pay freight</label>
                                    <label className="text-sm text-slate-600 flex items-center gap-2"><input type="checkbox" checked={editCustForm.apptRequired} onChange={(e) => setEditCustForm({ ...editCustForm, apptRequired: e.target.checked })} /> Delivery appointment required</label>
                                  </div>
                                  <textarea value={editCustForm.deliveryInstructions} onChange={(e) => setEditCustForm({ ...editCustForm, deliveryInstructions: e.target.value })} placeholder="Delivery instructions (appointment booking, windows…)" className="input w-full min-h-[52px]" />
                                  <textarea value={editCustForm.invoiceText} onChange={(e) => setEditCustForm({ ...editCustForm, invoiceText: e.target.value })} placeholder='Their required invoice email wording — use XXX where the PO number goes' className="input w-full min-h-[52px]" />
                                  <input value={editCustForm.deliveryAddress} onChange={(e) => setEditCustForm({ ...editCustForm, deliveryAddress: e.target.value })} placeholder="Delivery address" className="input w-full" />
                                  <div className="flex justify-end gap-2">
                                    <button onClick={() => setEditCustId(null)} className="btn btn-ghost">Cancel</button>
                                    <button onClick={() => saveCustomer.mutate()} className="btn btn-primary" disabled={!editCustForm.name.trim() || saveCustomer.isPending}>{saveCustomer.isPending ? 'Saving…' : 'Save'}</button>
                                  </div>
                                </div>
                              </td></tr>
                            )}
                          </Fragment>
                        );
                      })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-400">Open A/R comes from the Invoices tab; last order from Orders. Click a row to edit the customer.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
